/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useRef, Suspense, useMemo, type ReactNode, Fragment } from 'react';
import { type AnalyticsWebClient } from '@atlassian/forge-ui/analytics';
import {
	ForgeErrorBoundary,
	type Extension,
	ForgeUIExtensionAnalyticsContext,
} from '@atlassian/forge-ui/ui';
import type { DocNode } from '@atlaskit/adf-schema';
import { isCustomUI } from '@atlassian/forge-ui';
import { fg } from '@atlaskit/platform-feature-flags';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { ForgeUIExtensionPointProvider } from '@atlassian/forge-ui/provider';
import type { ForgeDoc, ProductEnvironment } from '@atlassian/forge-ui-types';
import { type ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type ApolloClient from 'apollo-client';
import { token } from '@atlaskit/tokens';
import { dequal } from 'dequal/lite';
import { useIntl } from 'react-intl-next';

import { getConfig } from '../config';
import { ForgeUIExtension, AdfExportRenderer } from './renderers';

import { isAdfExportEntrypoint } from '../utils/pdfEntrypointUtils';
import type { FlagFunctions } from '../utils/getFlagProvider';
import {
	type CustomEditContext,
	type ForgeExtension,
	type EditorAPI,
	type ForgeExtensionParameters,
} from '../types';
import {
	calculateViewportSizeHeight,
	type ViewportSizeType,
} from './renderers/calculateViewportSizeHeight';
import { FORGE_DOC_UPDATED_EVENT } from '../utils/constants';
import { useGetStaticForgeDoc } from './hooks/useGetStaticForgeDoc';

const styles1 = css({
	position: 'relative',
});

const styles2 = css({
	background: 'transparent',
	cursor: 'not-allowed',
	left: 0,
	position: 'absolute',
	height: '100%',
	top: 0,
	width: '100%',
	zIndex: 1,
});

interface ReservedHeightWrapperProps {
	children: ReactNode;
	extension: Extension;
}

const ReservedHeightWrapper = ({ extension, children }: ReservedHeightWrapperProps) => {
	const extensionViewportSize = extension?.properties?.viewportSize as ViewportSizeType | undefined;

	// Only apply reserved height if the feature flag is on AND actualViewportSize is defined
	if (extensionViewportSize && fg('confluence_forge_early_render_reserve_height')) {
		const viewportSizeHeight = calculateViewportSizeHeight(extensionViewportSize);

		// Custom UI extensions have full support for viewportSize, leading to a strict height.
		// UI Kit 2 extensions, while they can define viewportSize in their manifest, primarily
		// have dynamic sizing. This means viewportSize is not a strict constraint for them as it
		// is for Custom UI. Using minHeight based on a manifest-defined viewportSize is therefore
		// a best-effort approach to proactively reserve space. This aims to reduce layout shift
		// while still allowing the extension to grow dynamically if its content requires more space.
		if (isCustomUI(extension)) {
			return (
				<div style={{ height: viewportSizeHeight }} data-testid="reserved-height-wrapper-custom-ui">
					{children}
				</div>
			);
		} else {
			return (
				<div style={{ minHeight: viewportSizeHeight }} data-testid="reserved-height-wrapper-uikit">
					{children}
				</div>
			);
		}
	}

	// If FF is off, or if actualViewportSize is undefined, render children directly
	return <Fragment>{children}</Fragment>;
};

export interface ExtensionRef {
	openConfig: () => Promise<{ [key: string]: any } | void>;
}

const APP_CONTAINER_PADDING = token('space.100', '8px');

export interface ForgeUIExtensionWrapperProps {
	accountId: string;
	analyticsWebClient: AnalyticsWebClient | Promise<AnalyticsWebClient>;
	apolloClient: ApolloClient<object>;
	cloudId: string;
	contextIds: string[];
	customEditContext?: CustomEditContext;
	dataProviders: ProviderFactory;
	editorAPI?: EditorAPI;
	environment: ProductEnvironment;
	extension: ForgeExtension;
	extensionData: Record<string, any>;
	flags: FlagFunctions;
	getContextToken?: () => Promise<string>;
	hideGlassPane?: boolean;
	isEditing: boolean;
	page: string;
	product: string;
	useRouteActions?: () => {
		[key: string]: (...args: any[]) => any;
		push: (url: string, forceReload?: boolean) => void;
	};
}

const useMemoedExtensionData = (extensionData: Record<string, any>): Record<string, any> => {
	const ref = useRef(extensionData);
	if (!dequal(ref.current, extensionData)) {
		ref.current = extensionData;
	}
	return ref.current;
};

const ForgeUIExtensionWrapper = ({
	apolloClient,
	contextIds,
	cloudId,
	environment,
	extensionData,
	editorAPI,
	accountId,
	dataProviders,
	extension,
	isEditing,
	flags,
	getContextToken,
	hideGlassPane,
	customEditContext,
	useRouteActions,
}: ForgeUIExtensionWrapperProps) => {
	const elRef = useRef<HTMLDivElement>(null);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	const isStaticMacro =
		extension.parameters?.extensionProperties?.extension?.properties?.content === 'static';

	const {
		extension: gqlExtension,
		extensionId,
		extensionTitle,
		measuredHeight,
		viewportHeight,
		// If `parameters` is missing, we'll let that bubble up to the extension's error boundary.
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	} = extension.parameters!;

	let localId = extension.parameters?.localId || '';
	/**
	 * Macros created by auto convert flow does not generate a `localId`. `localId` is being created by `quickInsert` in convertToExtensionManifest.tsx and is stored in the extension object
	 * Extract `localId` here to be passed into `coreData` below.
	 *
	 */
	if (!localId) {
		localId = extension.localId as string;
	}

	const viewportDimensions = useMemo(
		() => (fg('platform_measure_macro_height') ? { measuredHeight, viewportHeight } : undefined),
		[measuredHeight, viewportHeight],
	);

	const config = getConfig(extension.parameters!);

	const isBodiedMacro = extension.type === 'bodiedExtension';
	const extensionViewData = useMemo(
		() =>
			isBodiedMacro && !!extension.content
				? {
						macro: {
							body: {
								type: 'doc' as const,
								version: 1 as const,
								content: extension.content as DocNode['content'],
							},
						},
					}
				: {},
		[extension.content, isBodiedMacro],
	);

	const isAdfExport = isAdfExportEntrypoint(gqlExtension as Extension);

	const isHostedResource = Boolean(gqlExtension?.properties?.resource);
	const isUiKitTwo = gqlExtension?.properties?.render === 'native';
	const isCustomUI = isHostedResource && !isUiKitTwo;
	// inline macro doesn't support Custom UI
	const isInline = gqlExtension?.properties?.layout === 'inline' && !isCustomUI;

	const coreData = { localId, cloudId };
	const macroExtensionData = {
		...(customEditContext
			? {
					isConfiguring: true,
					isInserting: customEditContext.isInserting,
				}
			: {}),
		...(isBodiedMacro ? { layout: 'bodied' } : {}),
	};

	const memoedExtensionData = useMemoedExtensionData(
		fg('platform_move_dynamic_macro_context')
			? { ...extensionData }
			: {
					...extensionData,
					isEditing,
					config,
					...(Object.keys(macroExtensionData).length ? { macro: macroExtensionData } : {}),
				},
	);

	const extensionPayload = useMemoedExtensionData({
		config,
		isEditing,
		...(Object.keys(macroExtensionData).length ? { macro: macroExtensionData } : {}),
	});

	const { staticForgeDoc, shouldRenderRuntime, storeStaticForgeDoc, unmountRuntime } =
		useGetStaticForgeDoc({
			editorAPI,
			extensionParameters: extension.parameters as ForgeExtensionParameters,
			macroConfig: extensionPayload?.config,
		});

	const { locale } = useIntl();

	const handleOnMacroUpdated = (forgeDoc?: ForgeDoc) => {
		if (
			isBodiedMacro &&
			forgeDoc?.children?.[0].type === 'AdfRenderer' &&
			FeatureGates.getExperimentValue(
				'confluence_toc_macro_compatibility_with_apps',
				'isEnabled',
				true,
			)
		) {
			const event = new CustomEvent(FORGE_DOC_UPDATED_EVENT, {
				detail: {
					forgeDoc,
					localId,
				},
				bubbles: true,
			});
			document.dispatchEvent(event);
		}

		// Store `staticForgeDoc` in macro parameters when:
		// - content behaviour is static
		// - is editing
		// - config is opened
		// - runtime is mounted
		// - a new forgeDoc has been generated
		// - the update is not a macro config update (required to prevent custom config from storing it's update)
		if (
			isStaticMacro &&
			isEditing &&
			shouldRenderRuntime &&
			forgeDoc &&
			customEditContext?.modalExtension?.type !== 'macroConfig' &&
			editorAPI &&
			localId &&
			fg('forge-ui-static-macros')
		) {
			storeStaticForgeDoc(forgeDoc);
		}

		if (fg('platform_measure_macro_height')) {
			const isContextPanelOpen =
				editorAPI?.extension?.sharedState?.currentState()?.showContextPanel;

			const actionsAPI = editorAPI?.extension?.actions?.api();

			// Check if the context panel is open to filter for macro config updates
			if (!actionsAPI || !isContextPanelOpen) {
				return;
			}

			// Only call this function in edit/live page mode
			if (elRef.current && isEditing) {
				if (timerRef.current) {
					clearTimeout(timerRef.current);
				}

				timerRef.current = setTimeout(() => {
					const rectHeight = elRef?.current?.getBoundingClientRect().height;
					// allow three seconds for any async effects
					try {
						/**
						 * This attribute will be Forge's effort to estimate and reserve rendered height
						 * to achieve 0 CLS on behalf of vendors. Alongside this change will be some
						 * heuristic measurements on view page to check the accuracy of this over time.
						 */
						actionsAPI.doc.update(localId, (currentNode) => {
							const newNode = {
								attrs: {
									...currentNode.attrs,
									parameters: {
										...currentNode.attrs?.parameters,
										measuredHeight: rectHeight,
									},
								},
							};

							return newNode;
						});
					} catch {}
				}, 3000);
			}
		}
	};

	const renderRenderer = () => {
		if (isAdfExport && isHostedResource) {
			/**
			 * For frontend macro rendering, we only pass the `extensionViewData` (macro body) to the iframe over the JS bridge.
			 * This is to avoid it being included in the Forge Invocation Token (FIT) for remote endpoint resolver calls.
			 * However, the `AdfExport` is a backend function, so we cannot pass it over the JS bridge.
			 * Nevertheless, it won't be included in the FIT, because the export does not use a remote endpoint resolver.
			 * It also won't be included in the Forge Compute Token (FCT), because the FCT only includes context--not `extensionPayload`.
			 */

			return (
				<AdfExportRenderer
					accountId={accountId}
					apolloClient={apolloClient}
					contextIds={contextIds}
					coreData={coreData}
					dataProviders={dataProviders}
					extension={gqlExtension as Extension}
					extensionData={memoedExtensionData}
					extensionPayload={
						fg('platform_move_dynamic_macro_context')
							? { ...extensionPayload, ...extensionViewData }
							: extensionViewData
					}
					extensionId={extensionId}
					extensionTitle={extensionTitle}
				/>
			);
		}

		return (
			<ReservedHeightWrapper extension={gqlExtension as Extension}>
				<ForgeUIExtension
					staticForgeDoc={
						shouldRenderRuntime ||
						// ensure runtime is rendered for custom config resource
						customEditContext?.modalExtension ||
						!fg('forge-ui-static-macros')
							? null
							: staticForgeDoc
					}
					unmountRuntime={unmountRuntime}
					accountId={accountId}
					apolloClient={apolloClient}
					contextIds={contextIds}
					environment={environment}
					extension={gqlExtension as Extension}
					coreData={coreData}
					extensionData={memoedExtensionData}
					extensionPayload={fg('platform_move_dynamic_macro_context') ? extensionPayload : {}}
					editorAPI={editorAPI}
					extensionViewData={extensionViewData}
					locale={locale}
					flags={flags}
					getContextToken={getContextToken}
					customEditContext={customEditContext}
					isEditing={isEditing}
					onForgeDocUpdated={handleOnMacroUpdated}
					viewportDimensions={viewportDimensions}
					useRouteActions={useRouteActions}
				/>
			</ReservedHeightWrapper>
		);
	};

	return (
		<div
			data-testid="ForgeExtensionContainer"
			style={{
				background: isEditing ? token('elevation.surface', '#fff') : 'transparent',
				padding: isEditing && !isInline ? APP_CONTAINER_PADDING : 0,
				display: isInline ? 'inline-block' : 'block',
				verticalAlign: isInline ? 'middle' : '',
			}}
			css={styles1}
			ref={elRef}
		>
			{renderRenderer()}
			{isEditing && !hideGlassPane && <div data-testid="GlassPane" css={styles2} />}
		</div>
	);
};

const ForgeUIExtensionWrapperWithBoundary = (props: ForgeUIExtensionWrapperProps) => {
	const { extension, product, environment, page, analyticsWebClient } = props;

	const children = (
		<ForgeErrorBoundary>
			<ForgeUIExtensionWrapper {...props} />
		</ForgeErrorBoundary>
	);

	return (
		// This Suspense fallback is for the ForgeErrorBoundary
		<Suspense fallback={null}>
			<ForgeUIExtensionPointProvider
				analyticsWebClient={analyticsWebClient}
				product={product}
				environment={environment}
				page={page}
				localId={extension.parameters?.localId}
			>
				{extension.parameters ? (
					<ForgeUIExtensionAnalyticsContext
						localId={extension.parameters.localId}
						extensionId={extension.parameters.extensionId}
					>
						{children}
					</ForgeUIExtensionAnalyticsContext>
				) : (
					children
				)}
			</ForgeUIExtensionPointProvider>
		</Suspense>
	);
};

export default ForgeUIExtensionWrapperWithBoundary;
